import { useState } from "react";
import { Link } from "@inertiajs/react";
import { TrendingUp, Users } from "lucide-react";
import ModeToggle from "@/components/shared/ModeToggle";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { formatRoom, formatGrade } from "@/lib/classroom";
import { formatMoney, formatNumber, pluralize } from "@/lib/format";

/**
 * Top-N student and classroom rankings for a campaign. Both cards are
 * shared between the school admin dashboard and the teacher classroom
 * dashboard so the school-wide rollup looks identical on both surfaces.
 *
 * Each card expects `{ by_money: [...], by_minutes: [...] }` — pre-sorted
 * dual rankings so the toggle never needs a refetch.
 */

export function TopStudentsCard({ students }) {
  const byMoney = students?.by_money || [];
  const byMinutes = students?.by_minutes || [];
  const [mode, setMode] = useState("money");
  if (byMoney.length === 0) return null;

  const isMoney = mode === "money";
  const list = isMoney ? byMoney : byMinutes;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              {isMoney ? "Top fundraisers" : "Top readers"}
            </CardTitle>
            <CardDescription>
              Top {list.length} students across the whole school, ranked by {isMoney ? "money raised" : "minutes read"}.
            </CardDescription>
          </div>
          <ModeToggle isMoney={isMoney} onMoney={() => setMode("money")} onMinutes={() => setMode("minutes")} />
        </div>
      </CardHeader>
      <CardContent>
        <ol className="space-y-2">
          {list.map((s, i) => (
            <li key={s.id} className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3">
              <Rank position={i + 1} />
              <div className="min-w-0 flex-1">
                {s.student_path ? (
                  <Link href={s.student_path} className="block truncate font-medium text-foreground hover:text-primary">
                    {s.full_name}
                  </Link>
                ) : (
                  <p className="truncate font-medium text-foreground">{s.full_name}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {formatRoom(s.classroom_name)} · {isMoney ? `${formatNumber(s.minutes_logged)} min` : formatMoney(s.raised_cents)}
                </p>
              </div>
              <div className="shrink-0 font-semibold text-primary font-tabular">
                {isMoney ? formatMoney(s.raised_cents) : `${formatNumber(s.minutes_logged)} min`}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

// A rank number in Fraunces; the top three in clay (DESIGN.md, leaderboards).
function Rank({ position }) {
  return (
    <span
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary font-display text-lg font-semibold font-tabular ${
        position <= 3 ? "text-primary" : "text-muted-foreground"
      }`}
    >
      {position}
    </span>
  );
}

export function TopClassroomsCard({ classrooms }) {
  const byMoney = classrooms?.by_money || [];
  const byMinutes = classrooms?.by_minutes || [];
  const [mode, setMode] = useState("money");
  if (byMoney.length === 0) return null;

  const isMoney = mode === "money";
  const list = isMoney ? byMoney : byMinutes;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Top classrooms
            </CardTitle>
            <CardDescription>
              Whole classroom totals, ranked by {isMoney ? "money raised" : "minutes read"}.
            </CardDescription>
          </div>
          <ModeToggle isMoney={isMoney} onMoney={() => setMode("money")} onMinutes={() => setMode("minutes")} />
        </div>
      </CardHeader>
      <CardContent>
        <ol className="space-y-2">
          {list.map((c, i) => (
            <li key={c.id} className="flex items-center gap-4 rounded-lg border border-border bg-card px-4 py-3">
              <Rank position={i + 1} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground">{formatRoom(c.name)}</p>
                <p className="text-xs text-muted-foreground">
                  {c.grade && `${formatGrade(c.grade)} · `}{pluralize(c.student_count, "student")} · {isMoney ? `${formatNumber(c.minutes_logged)} min` : formatMoney(c.raised_cents)}
                </p>
              </div>
              <div className="shrink-0 font-semibold text-primary font-tabular">
                {isMoney ? formatMoney(c.raised_cents) : `${formatNumber(c.minutes_logged)} min`}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
